import type { LoginOption } from './loginConstants';

/** Main landing login is private-client only — OTP quick sign-in and Google/Apple. */
export const MAIN_PORTAL_LOGIN_TYPE = 'private client' as const satisfies LoginOption;

/** Canonical asset-owner login type (role 4). Legacy private/business labels still sign in. */
export const ASSET_OWNER_LOGIN_TYPE = 'asset owner' as const satisfies LoginOption;

/** Offizz role ids assigned by register routes (see RegisterTypeMenu). */
export const EXPECTED_SERVER_ROLE_ID_BY_LOGIN_TYPE: Partial<Record<LoginOption, number>> = {
  'private client': 1,
  'business client': 2,
  'business worker': 3,
  'asset owner': 4,
  'private asset owner': 4,
  'business asset owner': 4,
};

/** Asset owners are role 4; accept 5 from older sessions until tokens expire. */
export const ASSET_OWNER_SERVER_ROLE_IDS = [4, 5] as const;

export function isMainPortalLoginType(loginType: string | null | undefined): boolean {
  return loginType === MAIN_PORTAL_LOGIN_TYPE;
}

export function isAssetOwnerLoginType(loginType: string | null | undefined): boolean {
  return (
    loginType === 'asset owner' ||
    loginType === 'business asset owner' ||
    loginType === 'private asset owner'
  );
}

/** Only private clients use quick OTP on the default login page. */
export function supportsMainPortalOtp(loginType: LoginOption): boolean {
  return loginType === MAIN_PORTAL_LOGIN_TYPE;
}

export function prefersGoogleRegisterFirst(loginType: LoginOption): boolean {
  return isMainPortalLoginType(loginType);
}

/** Google lite-register is allowed for every register/login option. */
export function supportsGoogleLiteRegister(loginType: LoginOption): boolean {
  return Boolean(loginType?.trim());
}

/** Email OTP lite-register on the main portal is private-client only. */
export function supportsOtpLiteRegister(loginType: LoginOption): boolean {
  return supportsMainPortalOtp(loginType);
}

export function serverRoleMatchesLoginType(loginType: LoginOption, roleId: number | null): boolean {
  if (roleId === null) {
    return true;
  }
  if (isAssetOwnerLoginType(loginType)) {
    return (ASSET_OWNER_SERVER_ROLE_IDS as readonly number[]).includes(roleId);
  }
  const expected = EXPECTED_SERVER_ROLE_ID_BY_LOGIN_TYPE[loginType];
  if (expected === undefined) {
    return true;
  }
  return roleId === expected;
}
